import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer } from "antd";
import { message } from "../../components/antdMessageBridge";
import { CloseCircleOutlined } from "@ant-design/icons";
import "../manifest_verificationqueue/verification-slider.css";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import CommonObj from "../../CommonObj";
import { useAuth } from "../../auth/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";

const ExternalApiSliderUI = (props) => {
    const [form, setForm] = useState({
        apiURL: "",
        apiKey: "",
        appName: "",
        sortOrder: "",
    });
    const { roleUserId } = useAuth();
    const { accessToken } = useAuthToken();
    const [loading, setLoading] = useState(false);
    const [ierrors, setIErrors] = useState({});
    const [selectedStatus, setSelectedStatus] = useState(true);

    const fetchRecordData = async (id) => {
        setLoading(true);
        const requestData = {
            ExternalAPIID: id,
            UserId: roleUserId,
        };

        let formData = new FormData();
        Object.keys(requestData).forEach(function (key) {
            formData.append(key, requestData[key]);
        });

        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetExternalAPIById, {
                method: "POST",
                headers: myHeaders,
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
                setForm({
                    apiURL: result.apiurl || "",
                    apiKey: result.apiKey || "",
                    appName: result.appName || "",
                    sortOrder: result.sortOrder !== undefined ? String(result.sortOrder) : "",
                });
                props.setSelectedApiName(result.appName || "");
                setSelectedStatus(result.isActive === 1);
            } catch (e) {
                if (!responseText.includes("No records found.")) {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!props.selectedRecord && props.visible) {
            setForm({
                apiURL: "",
                apiKey: "",
                appName: "",
                sortOrder: "",
            });
            setSelectedStatus(true);
            setIErrors({});
        }
    }, [props.visible, props.selectedRecord]);

    useEffect(() => {
        if (props.selectedRecord && props.visible) {
            fetchRecordData(props.selectedRecord.externalAPIID);
        }
    }, [props.selectedRecord, props.visible]);

    const handleInternalSubmit = async (e) => {
        e.preventDefault();
        setIErrors({});

        if (!i_validate()) {
            return;
        }
        setLoading(true);
        try {
            const isEdit = !!props.selectedRecord;

            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };

            const payload = isEdit
                ? {
                    ExternalAPIID: props.selectedRecord.externalAPIID,
                    APIURL: form.apiURL,
                    APIKey: form.apiKey,
                    AppName: form.appName,
                    SortOrder: form.sortOrder,
                    IsActive: selectedStatus ? 1 : 0,
                    UserId: roleUserId,
                }
                : {
                    APIURL: form.apiURL,
                    APIKey: form.apiKey,
                    AppName: form.appName,
                    SortOrder: form.sortOrder,
                    UserId: roleUserId,
                };

            let formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const endpoint = isEdit
                ? CommonObj.Endpoints.UpdateExternalAPI
                : CommonObj.Endpoints.CreateExternalAPI;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: myHeaders,
                body: formData,
            });

            if (!response.ok) throw new Error(`Response status: ${response.status}`);

            const result = await response.json();
            if (result.status === false) {
                const ErrorMsg = result.message !== "" ? result.message : "Failed to save the data";
                message.error(ErrorMsg);
                return;
            }

            if (result) {
                const successMsg = isEdit
                    ? "External API updated successfully."
                    : "External API created successfully.";

                message.success(successMsg);
                handleClear();
                props.onClose();

                if (props.onSuccess) {
                    props.onSuccess();
                }
            } else {
                const msg = result.message !== "" ? result.message : "Failed to save the data";
                message.error(msg);
            }
        } catch (error) {
            message.error("Error submitting form: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setForm({
            apiURL: "",
            apiKey: "",
            appName: "",
            sortOrder: "",
        });
        props.setSelectedApiName("");
        setSelectedStatus(true);
        setIErrors({});
    };

    const i_validate = () => {
        const newErrors = {};
        if (!form.apiURL || !form.apiURL.trim()) {
            newErrors.apiURL = "Please enter API URL";
        }
        if (!form.apiKey || !form.apiKey.trim()) {
            newErrors.apiKey = "Please enter API Key";
        }
        if (!form.appName || !form.appName.trim()) {
            newErrors.appName = "Please enter App Name";
        }
        if (!form.sortOrder || !String(form.sortOrder).trim()) {
            newErrors.sortOrder = "Please enter Sort Order";
        }
        setIErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInternalChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));

        let errorMsg = "";
        if (name === "apiURL") {
            errorMsg = !value || !value.trim() ? "Please enter API URL" : "";
        } else if (name === "apiKey") {
            errorMsg = !value || !value.trim() ? "Please enter API Key" : "";
        } else if (name === "appName") {
            errorMsg = !value || !value.trim() ? "Please enter App Name" : "";
        } else if (name === "sortOrder") {
            errorMsg = !value || !String(value).trim() ? "Please enter Sort Order" : "";
        }
        setIErrors((prev) => ({ ...prev, [name]: errorMsg }));
    };

    const handlestatusChange = (e) => {
        setSelectedStatus(e.target.checked);
    };

    const handleClose = () => {
        setIErrors({});
        props.onClose();
    };

    return (
        <Drawer
            title={props.selectedRecord ? "Edit External API" : props.title}
            placement={props.placement}
            onClose={handleClose}
            open={props.visible}
            width={props.width}
            className={props.className}
            closeIcon={<CloseCircleOutlined />}
            loading={loading}
        >
            <div className="modal-body">
                <div className="">
                    <form
                        className="book-page-form"
                        onSubmit={handleInternalSubmit}
                        noValidate
                    >
                        <div className="add-user-internal-container">
                            <div className="add-user-internal-input-2column">
                                <div className="field-group">
                                    <input
                                        placeholder=""
                                        id="apiURL"
                                        name="apiURL"
                                        value={form.apiURL}
                                        className={`book-page-form-input-text ${form.apiURL === "" ? "" : "has-text"}`}
                                        maxLength={500}
                                        onChange={handleInternalChange}
                                        required
                                    />
                                    <label htmlFor="apiURL" className="label">
                                        API URL <span className="form-required">*</span>
                                    </label>
                                    {ierrors.apiURL && (
                                        <span className="error-msg">{ierrors.apiURL}</span>
                                    )}
                                </div>
                                <div className="field-group">
                                    <input
                                        placeholder=""
                                        id="apiKey"
                                        name="apiKey"
                                        value={form.apiKey}
                                        className={`book-page-form-input-text ${form.apiKey === "" ? "" : "has-text"}`}
                                        maxLength={200}
                                        onChange={handleInternalChange}
                                        required
                                    />
                                    <label htmlFor="apiKey" className="label">
                                        API Key <span className="form-required">*</span>
                                    </label>
                                    {ierrors.apiKey && (
                                        <span className="error-msg">{ierrors.apiKey}</span>
                                    )}
                                </div>
                            </div>
                            <div className="add-user-internal-input-2column">
                                <div className="field-group">
                                    <input
                                        placeholder=""
                                        id="appName"
                                        name="appName"
                                        value={form.appName}
                                        className={`book-page-form-input-text ${form.appName === "" ? "" : "has-text"}`}
                                        maxLength={200}
                                        onChange={handleInternalChange}
                                        required
                                    />
                                    <label htmlFor="appName" className="label">
                                        App Name <span className="form-required">*</span>
                                    </label>
                                    {ierrors.appName && (
                                        <span className="error-msg">{ierrors.appName}</span>
                                    )}
                                </div>
                                <div className="field-group">
                                    <input
                                        placeholder=""
                                        id="sortOrder"
                                        name="sortOrder"
                                        value={form.sortOrder}
                                        className={`book-page-form-input-text ${form.sortOrder === "" ? "" : "has-text"}`}
                                        maxLength={10}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, "");
                                            handleInternalChange({ target: { name: "sortOrder", value } });
                                        }}
                                        required
                                    />
                                    <label htmlFor="sortOrder" className="label">
                                        Sort Order <span className="form-required">*</span>
                                    </label>
                                    {ierrors.sortOrder && (
                                        <span className="error-msg">{ierrors.sortOrder}</span>
                                    )}
                                </div>
                            </div>
                            {props?.selectedRecord && (
                                <div className="book-page-form-checkbox-button">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        name="isActive"
                                        onChange={handlestatusChange}
                                        checked={selectedStatus || undefined}
                                    />
                                    <label htmlFor="isActive">Is Active</label>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="modal-cancel-button" onClick={handleClear}>
                                Clear
                            </button>
                            <button className="accept-button">Submit</button>
                        </div>
                    </form>
                </div>
            </div>
        </Drawer>
    );
};

ExternalApiSliderUI.propTypes = {
    selectedRecord: PropTypes.shape({
        externalAPIID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    visible: PropTypes.bool,
    title: PropTypes.string,
    placement: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    onSuccess: PropTypes.func,
    setSelectedApiName: PropTypes.func,
    selectedApiName: PropTypes.string,
};

export default ExternalApiSliderUI;
